// File: Home.js
import React, { useEffect, useRef, useState } from "react";
import "./Home.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartLine,
  faFileInvoiceDollar,
  faBoxesStacked,
  faUsers,
  faShieldHalved,
  faCloudArrowUp,
  faMobileScreenButton,
  faGears,
  faBolt,
  faCheckCircle,
  faArrowRight,
  faBarcode,
  faWarehouse,
  faMoneyBillTransfer,
  faRocket,
  faStar,
  faHeadset,
  faBuildingColumns,
  faChartPie,
  faReceipt,
  faBell,
  faScaleBalanced ,
  faPrint,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { faIndianRupeeSign } from "@fortawesome/free-solid-svg-icons";
import {
  faShoppingCart,
  faCapsules,
  faShirt,
  faShoePrints,
  faIndustry,
  faCar,
  faTree,
  faToilet,
  faBook,
  faGem,
  faUtensils,
  faMugHot,
  faHotel,
  faHospital,
  faMobileAlt,
  faLaptop,
  faCouch,
  faBuilding,
  faTruck,
  faSeedling,
  faBreadSlice,
  faDrumstickBite,
  faGasPump,
  faWrench,
  faCamera,
  faDumbbell,
  faSpa,
  faSchool,
  faUniversity,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// ─── Stats ────────────────────────────────────────────────────
const STATS = [
  { value: "100+", label: "Industries Served" },
  { value: "5000+", label: "Active Businesses" },
  { value: "99.9%", label: "Uptime Guarantee" },
  { value: "24/7", label: "Expert Support" },
];

// ─── Hero Feature Pills ────────────────────────────────────────
const HERO_PILLS = [
  { icon: faBarcode, label: "Barcode Billing" },
  { icon: faFileInvoiceDollar, label: "GST Ready" },
  { icon: faWhatsapp, label: "WhatsApp Reports" },
  { icon: faChartLine, label: "Live Analytics" },
  { icon: faCloudArrowUp, label: "Cloud Sync" },
  { icon: faMobileScreenButton, label: "Mobile App" },
];

// ─── Core Modules ─────────────────────────────────────────────
const MODULES = [
  { icon: faFileInvoiceDollar, title: "Smart Billing", desc: "Fast POS billing with barcode scanning, multi-payment modes, and auto GST calculation.", color: "blue" },
  { icon: faBoxesStacked, title: "Inventory Control", desc: "Real-time stock tracking, low-stock alerts, batch management, and dead-stock reports.", color: "teal" },
  { icon: faChartLine, title: "Sales Analytics", desc: "Live dashboards, sales trends, item-wise profit reports, and branch comparisons.", color: "purple" },
  { icon: faUsers, title: "CRM & Loyalty", desc: "Customer profiles, loyalty points, birthday offers, and WhatsApp communication.", color: "coral" },
  { icon: faWarehouse, title: "Purchase & Godown", desc: "Supplier management, purchase orders, GRN, and multi-warehouse stock transfers.", color: "amber" },
  { icon: faShieldHalved, title: "GST Compliance", desc: "Auto GSTR-1, GSTR-3B filing, HSN mapping, e-invoice, and e-way bill generation.", color: "green" },
  { icon: faWhatsapp, title: "WhatsApp Integration", desc: "Send bills, payment reminders, offers, and reports directly via WhatsApp Business API.", color: "teal" },
  { icon: faCloudArrowUp, title: "Cloud & Backup", desc: "Secure AWS cloud backup, anytime access, automatic sync across all your branches.", color: "blue" },
  { icon: faMobileScreenButton, title: "Mobile Access", desc: "Android app for owners — view live sales, approve purchases, check stock on the go.", color: "purple" },
  { icon: faBuildingColumns, title: "Multi-Branch", desc: "Manage 10+ branches from one dashboard — consolidated reports, stock transfer, and more.", color: "coral" },
  { icon: faPrint, title: "Label Printing", desc: "Customizable barcode, price, MRP labels — print in bulk or on-demand at the counter.", color: "amber" },
  { icon: faMoneyBillTransfer, title: "Accounts & Finance", desc: "Full double-entry accounting, P&L, balance sheet, cash flow, and day-book reports.", color: "green" },
];

// ─── Why Choose ───────────────────────────────────────────────
const WHY_ITEMS = [
  { icon: faBolt, title: "Built for Indian Retail", desc: "Deep GST compliance, Hindi/regional language support, and Indian payment gateway integrations." },
  { icon: faRocket, title: "Go Live in 1 Day", desc: "Simple onboarding, data migration from your existing software, and free setup assistance." },
  { icon: faHeadset, title: "Dedicated Support", desc: "Dedicated account manager, WhatsApp support, remote training, and annual on-site visits." },
  { icon: faGlobe, title: "Scales with You", desc: "From single shop to 50+ branch enterprise — the same software grows with your business." },
  { icon: faShieldHalved, title: "Bank-Grade Security", desc: "Role-based access, audit trail, encrypted backups, and daily cloud snapshots." },
  { icon: faGears, title: "Customizable Workflows", desc: "Configure billing formats, approval workflows, and reports to match your exact process." },
];

// ─── Industries ───────────────────────────────────────────────
const INDUSTRIES = [
  { icon: faShoppingCart, name: "Supermarkets" },
  { icon: faCapsules, name: "Pharmacy / Medical Stores" },
  { icon: faShirt, name: "Garments / Fashion Stores" },
  { icon: faShoePrints, name: "Footwear" },
  { icon: faIndustry, name: "Textile / Mills" },
  { icon: faGem , name: "Jewellery" },
  { icon: faCar, name: "Automobile Showrooms" },
  { icon: faBoxesStacked, name: "FMCG Distribution" },
  { icon: faBolt, name: "Electrical & Hardware" },
  { icon: faTree, name: "Plywood & Timber" },
  { icon: faToilet, name: "Sanitary & Plumbing" },
  { icon: faBook, name: "Books & Stationery" },
  { icon: faGem, name: "Jewellery" },

  { icon: faUtensils, name: "Restaurants" },
  { icon: faMugHot, name: "Cafes & Bakeries" },
  { icon: faHotel, name: "Hotels & Lodges" },
  { icon: faHospital, name: "Hospitals & Clinics" },

  { icon: faMobileAlt, name: "Mobile Stores" },
  { icon: faLaptop, name: "Electronics & Appliances" },
  { icon: faCouch, name: "Furniture Stores" },
  { icon: faBuilding, name: "Real Estate & Builders" },

  { icon: faTruck, name: "Logistics & Transport" },
  { icon: faWarehouse, name: "Warehousing & Godowns" },

  { icon: faSeedling, name: "Agriculture & Fertilizers" },
  { icon: faBreadSlice, name: "Bakeries" },
  { icon: faDrumstickBite, name: "Meat & Poultry" },

  { icon: faGasPump, name: "Petrol Bunks" },
  { icon: faWrench, name: "Repair & Service Centers" },

  { icon: faPrint, name: "Printing Press" },
  { icon: faCamera, name: "Photography Studios" },

  { icon: faDumbbell, name: "Gyms & Fitness Centers" },
  { icon: faSpa, name: "Salons & Spa" },

  { icon: faSchool, name: "Schools" },
  { icon: faUniversity, name: "Colleges & Institutions" },

  { icon: faStore, name: "Retail Shops (General Stores)" }
];
// ─── Testimonials ─────────────────────────────────────────────
const TESTIMONIALS = [
  {
    name: "Ramesh Agarwal",
    industry: "Supermarket",
    text: "Billing speed doubled after switching to Auditus-iERP. The WhatsApp invoice feature alone saves us 2 hours every day.",
    stars: 5,
  },
  {
    name: "Sunita Reddy",
    industry: "Pharmacy",
    text: "Expiry tracking and batch management is flawless. GST filing that used to take a week now takes one click.",
    stars: 5,
  },
  {
    name: "Mohammed Farooq",
    industry: "Textile",
    text: "Managing 3 branches was a nightmare before. Now I see consolidated stock and sales in real time on my phone.",
    stars: 5,
  },
  {
    name: "Priya Lakshmi",
    industry: "Garments",
    text: "The size-colour matrix billing is perfect for fashion retail. Customer loyalty programme brought 30% repeat buyers.",
    stars: 5,
  },
];

// ─── Workflow Steps ───────────────────────────────────────────
const WORKFLOW = [
  { step: "01", title: "Quick Onboarding", desc: "Share your existing data — we migrate everything for free in under 24 hours." },
  { step: "02", title: "Configure Your Store", desc: "Set up products, GST slabs, billing formats, and user roles in minutes." },
  { step: "03", title: "Start Billing", desc: "Scan & bill instantly at the counter with barcode or product search." },
  { step: "04", title: "Grow with Insights", desc: "Track profit, manage stock, and grow with real-time dashboards." },
];

const APPS = [
  {
    title: "AUDITRACK",
    subtitle: "Stock Audit App",
    color: "green",
    img: "/images/mobile-1_Auditrack.png",
    features: [
      "Barcode-wise stock check",
      "Item-wise verification",
      "Location/Godown tracking",
      "Real-time stock validation",
      "Reduce manual errors"
    ],
    best: "Warehouses, Distributors"
  },
  {
    title: "ORDERFLOW",
    subtitle: "Order Management",
    color: "blue",
    img: "/images/mobile-2_orderFlow.png",
    features: [
      "Salesman order booking",
      "Real-time order sync",
      "Live stock availability",
      "Faster billing process",
      "Reduce order booking time"
    ],
    best: "Sales Team, Retailers"
  },
  {
    title: "COLLECTPRO",
    subtitle: "Collection App",
    color: "purple",
    img: "/images/mobile-3_Collectpro.png",
    features: [
      "Collection agent tracking",
      "Customer outstanding",
      "Payment updates real-time",
      "Daily reports",
      "Reduce pending payments"
    ],
    best: "Collection Agents"
  },
  {
    title: "BIZCONTROL",
    subtitle: "Owner App",
    color: "teal",
    img: "/images/mobile-4_Bizcontrol.png",
    features: [
      "Sales & Orders view",
      "Stock & inventory",
      "Reports & analytics",
      "Business overview",
      "Access anywhere"
    ],
    best: "Business Owners"
  },
  {
    title: "WHATSAPP",
    subtitle: "Integration",
    color: "orange",
    img: "/images/mobile-5_Whatsappintegration.png",
    features: [
      "Send invoices",
      "Order updates",
      "Payment reminders",
      "Customer communication",
      "Increase satisfaction"
    ],
    best: "All Businesses"
  }
];

// ─── Component ────────────────────────────────────────────────
export default function Home() {
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const heroRef = useRef(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 4500);
    return () => clearInterval(timer);
  }, []);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("visible");
          }
        });
      },
      { threshold: 0.1 }
    );
    document.querySelectorAll(".reveal").forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const [currentSlide, setCurrentSlide] = useState(0);

const nextSlide = () => {
  setCurrentSlide((prev) => (prev + 1) % APPS.length);
};

const prevSlide = () => {
  setCurrentSlide((prev) =>
    prev === 0 ? APPS.length - 1 : prev - 1
  );
};
  return (
    <div className="home-root">

      {/* ── HERO ─────────────────────────────────────────────── */}
      <section className="hero-section" ref={heroRef}>
        <div className="hero-bg-grid" />
        <div className="hero-glow hero-glow-1" />
        <div className="hero-glow hero-glow-2" />

        <div className="hero-inner">
          <div className="hero-badge">
            <span className="hero-badge-dot" />
            India's #1  Software for All Types of Industries
          </div>

          <h1 className="hero-h1">
            Run Your Entire Business<br />
            <span className="hero-accent">Smarter, Faster,</span>{" "}
            <span className="hero-accent-2">Profitable</span>
          </h1>

          <p className="hero-sub">
            Auditus-iERP is the all-in-one ERP trusted by 5,000+ Indian businesses —
            Retail , Distribution and Manufacturing. Barcode billing,
            GST compliance, WhatsApp integration, multi-branch control, and cloud analytics,
            all in one powerful platform.
          </p>

          <div className="hero-ctas">
            <a href="/Demo" className="btn-primary">
              Get Free Demo <FontAwesomeIcon icon={faArrowRight} />
            </a>
            <a href="#features" className="btn-outline">
              Explore Features
            </a>
          </div>

          <div className="hero-pills">
            {HERO_PILLS.map((p) => (
              <span key={p.label} className="hero-pill">
                <FontAwesomeIcon icon={p.icon} className="pill-icon" />
                {p.label}
              </span>
            ))}
          </div>
        </div>

        {/* Hero visual — dashboard mockup */}
        <div className="hero-visual">
          <div className="dashboard-mock">
            <div className="mock-topbar">
              <span className="mock-logo">Auditus-iERP</span>
              <div className="mock-dots">
                <span /><span /><span />
              </div>
            </div>
            <div className="mock-stats-row">
              {[
                { label: "Today's Sales", val: "₹1,24,580", up: true },
                { label: "Orders", val: "342", up: true },
                { label: "Pending Bills", val: "18", up: false },
              ].map((s) => (
                <div key={s.label} className="mock-stat">
                  <span className="mock-stat-label">{s.label}</span>
                  <span className="mock-stat-val">{s.val}</span>
                  <span className={`mock-stat-tag ${s.up ? "up" : "down"}`}>
                    {s.up ? "▲ 12%" : "▼ 3%"}
                  </span>
                </div>
              ))}
            </div>
            <div className="mock-chart-area">
              <div className="mock-chart-label">Sales this week</div>
              <div className="mock-bars">
                {[55, 78, 62, 90, 72, 95, 84].map((h, i) => (
                  <div key={i} className="mock-bar-wrap">
                    <div className="mock-bar" style={{ height: `${h}%` }} />
                    <span className="mock-bar-day">{["M","T","W","T","F","S","S"][i]}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="mock-bottom-row">
              <div className="mock-pill-item">
                <FontAwesomeIcon icon={faCheckCircle} className="mpill-icon green" />
                GST Filed
              </div>
              <div className="mock-pill-item">
                <FontAwesomeIcon icon={faBell} className="mpill-icon amber" />
                3 Low Stock
              </div>
              <div className="mock-pill-item">
                <FontAwesomeIcon icon={faWhatsapp} className="mpill-icon teal" />
                24 Sent
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STATS BAND ───────────────────────────────────────── */}
      <section className="stats-band">
        {STATS.map((s) => (
          <div key={s.label} className="stat-item">
            <span className="stat-value">{s.value}</span>
            <span className="stat-label">{s.label}</span>
          </div>
        ))}
      </section>

      {/* ── DESKTOP SOFTWARE SECTION ───────────────────────── */}
<section className="desktop-section reveal">

  <div className="section-header">
    <div className="section-tag">Desktop Software</div>
    <h2 className="section-h2">
      Powerful <span>Auditus-iERP Desktop</span>
    </h2>
  </div>

  <div className="desktop-main">

    {/* LEFT IMAGE */}
    <div className="desktop-img">
      <img 
        src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80" 
        alt="Auditus ERP Dashboard"
      />
    </div>

    {/* RIGHT CONTENT */}
    <div className="desktop-content">

      <h3>Complete Business Control in One Dashboard</h3>

      <p>
        Auditus-iERP desktop software gives you complete control over your business with
        a powerful dashboard, advanced inventory tracking, GST compliance, and real-time analytics.
        Designed for Indian businesses, it simplifies billing, stock management, and reporting.
      </p>

      <p>
        From multi-branch management to automated GST filing and detailed financial insights,
        everything is available in one unified system.
      </p>

    </div>

  </div>

  {/* SMALL IMAGES GRID */}
  <div className="desktop-features">

    <div className="desktop-mini">
      <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=400&q=80" />
      <span>Dashboard</span>
    </div>

<div className="desktop-mini">
  <img src="https://images.openai.com/static-rsc-4/tOOoGNotiJet0_2_b3sAOheRcZGWY5hMXLitPgR4XlB0OKQr_ybU0i8Xk944d-uNqEEj1fBWQx0SwaulTR-eV8hJ_ZB1TAzTG5R2K7Uc7-bsU0s8-R9BXywd-_oZhh08pBccSAmCCgnqrrsy9MMBy-ecpmRl7mbQRzeZzceEK1YQqP17sGRp7LSyFcatQNPf?purpose=fullsize" />
  <span>Sales Reports</span>
</div>

<div className="desktop-mini">
  <img src="https://images.openai.com/static-rsc-4/p5pXd2c-OAevRBGHMvfDP1vhObTgFQdeSjQmOphDgVKFnTj60u4ZPbSYxy35uDzTgBtP5gA2vKII4lE6K0tXllWJQd1KIsdv94ll0pYnxyHPsmOKk98LNMjyBlVy82ACdcOvHZ66ZugGuck4zdguEyt3XjFYABxVcd6k5T6RbVYKHgO-PfY73oVMqre-o-JE?purpose=fullsize" />
  <span>GST Reports</span>
</div>
    <div className="desktop-mini">
      <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?w=400&q=80" />
      <span>Inventory Tracking</span>
    </div>

  </div>

</section>
   {/* ── PREMIUM GST SECTION ───────────────────────── */}
<section className="gst-pro-section reveal">

  <div className="gst-pro-container">

    {/* LEFT TEXT */}
    <div className="gst-pro-left">
      <h1>
        Complete <span>GST Solution</span><br />
        For Every Business
      </h1>

      <p>
        One powerful platform to manage billing, returns, compliance, 
        reconciliation, and reports — fully automated & error-free.
      </p>

      <div className="gst-tags">
        <span>✔ One Click Filing</span>
        <span>✔ Auto Reconciliation</span>
        <span>✔ 100% GST Compliant</span>
      </div>
    </div>

    {/* CENTER IMAGE (REAL FROM WEB) */}
    <div className="gst-pro-center">
      <img
        src="https://images.unsplash.com/photo-1556155092-490a1ba16284?w=900&q=80"
        alt="GST Dashboard"
      />
    </div>

    {/* RIGHT CARD */}
    <div className="gst-pro-right">
      <div className="gst-badge">
        100% GST COMPLIANT
      </div>

      <div className="gst-card">
        <h3>Auditus-iERP</h3>
        <p>All-in-One GST Management</p>

     <a href="/Demo">   <button>Get Started →</button></a>
      </div>
    </div>

  </div>

  {/* GST RETURNS GRID */}
{/* GST RETURNS GRID */}
<div className="gst-returns">

  {[
    {
      name: "GSTR-1",
      desc: "File your outward supplies with accuracy",
      icon: faIndianRupeeSign,
      color: "blue"
    },
    {
      name: "GSTR-3B",
      desc: "Easy & accurate monthly return filing",
      icon: faChartLine,
      color: "green"
    },
    {
      name: "GSTR-2B",
      desc: "Auto-fetch & reconcile input tax credits",
      icon: faIndianRupeeSign,
      color: "purple"
    },
    {
      name: "GSTR-9",
      desc: "Annual return made simple & accurate",
      icon: faReceipt,
      color: "orange"
    },
    {
      name: "GSTR-9C",
      desc: "Annual Reconciliation Statement with ease",
      icon: faChartPie,
      color: "teal"
    },
    {
      name: "GSTR-4",
      desc: "For Composition Dealers - Simple and Fast",
      icon: faBoxesStacked,
      color: "pink"
    },
    {
      name: "GST DRC-03",
      desc: "Responsed to Notices & Demand Orders efficiently",
      icon: faScaleBalanced,
      color: "red"
    }
  ].map((item, i) => (
    <div key={i} className={`gst-return-card theme-${item.color}`}>

      <div className="gst-icon">
        <FontAwesomeIcon icon={item.icon} />
      </div>

      <div className="gst-text">
        <span className="gst-title">{item.name}</span>
        <small className="gst-desc">{item.desc}</small>
      </div>

    </div>
  ))}

</div>
{/* FEATURES */}
<div className="gst-services">

  {[
    { name: "E-Invoice", icon: faFileInvoiceDollar, color: "blue" },
    { name: "E-Way Bill", icon: faTruck, color: "green" },
    { name: "GST Reconciliation", icon: faChartLine, color: "purple" },
    { name: "ITC Management", icon: faMoneyBillTransfer, color: "orange" },
    { name: "Ledger & Reports", icon: faChartPie, color: "teal" },
    { name: "Audit & Compliance", icon: faShieldHalved, color: "red" }
  ].map((item, i) => (
    <div key={i} className={`gst-service theme-${item.color}`}>

      <FontAwesomeIcon icon={item.icon} className="gst-service-icon" />

      <span>{item.name}</span>

    </div>
  ))}

</div>

</section>

   {/* ── MOBILE APPS SECTION ───────────────────────── */}
<section className="apps-wide-section reveal">

  <div className="section-header">
    <div className="section-tag">Auditusierp Mobile Apps</div>
    <h2 className="section-h2">
      Powerful <span>Mobile App Suit</span>
    </h2>
  </div>

  <div className="apps-wide-grid">

    {APPS.map((app, i) => (
      <div key={i} className={`app-wide-card theme-${app.color}`}>

        {/* IMAGE */}
        <div className="app-wide-img">
          <img src={app.img} alt={app.title} />
        </div>

        {/* CONTENT */}
        <div className="app-wide-content">

          <h3>{app.title}</h3>
          <p className="sub">{app.subtitle}</p>

          <ul>
            {app.features.slice(0, 4).map((f, idx) => (
              <li key={idx}>✔ {f}</li>
            ))}
          </ul>

          <div className="best">
            Best for: <span>{app.best}</span>
          </div>

        </div>

      </div>
    ))}

  </div>

</section>
      {/* ── 12 MODULES GRID ──────────────────────────────────── */}
      <section className="modules-section" id="features">
        <div className="section-header reveal">
          <div className="section-tag">Core Modules</div>
          <h2 className="section-h2">Everything Your Business Needs<br /><span>in One Platform</span></h2>
          <p className="section-desc">12 powerful modules designed for Indian retail, working seamlessly together.</p>
        </div>
        <div className="modules-grid">
          {MODULES.map((m, i) => (
            <div key={m.title} className={`module-card reveal color-${m.color}`} style={{ animationDelay: `${i * 0.05}s` }}>
              <div className="module-icon-wrap">
                <FontAwesomeIcon icon={m.icon} className="module-icon" />
              </div>
              <h3 className="module-title">{m.title}</h3>
              <p className="module-desc">{m.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── HOW IT WORKS ─────────────────────────────────────── */}
      <section className="workflow-section">
        <div className="section-header reveal">
          <div className="section-tag">How It Works</div>
          <h2 className="section-h2">Up and Running <span>in 4 Simple Steps</span></h2>
        </div>
        <div className="workflow-track">
          {WORKFLOW.map((w, i) => (
            <div key={w.step} className="workflow-step reveal">
              <div className="workflow-num">{w.step}</div>
              {i < WORKFLOW.length - 1 && <div className="workflow-line" />}
              <h3 className="workflow-title">{w.title}</h3>
              <p className="workflow-desc">{w.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── INDUSTRIES ───────────────────────────────────────── */}
      <section className="industries-section">
        <div className="section-header reveal">
          <div className="section-tag">Industries</div>
          <h2 className="section-h2">Built for <span>All Types of Industries</span></h2>
          <p className="section-desc">Purpose-built features for every type of business — not a generic solution.</p>
        </div>
        <div className="industries-grid">
          {INDUSTRIES.map((ind) => (
            <div key={ind.name} className="industry-card reveal">
        <FontAwesomeIcon icon={ind.icon} className="industry-icon" />
              <span className="industry-name">{ind.name}</span>
            </div>
          ))}
        </div>
      </section>

      {/* ── WHY CHOOSE ───────────────────────────────────────── */}
      <section className="why-section">
        <div className="why-inner">
          <div className="why-left reveal">
            <div className="section-tag">Why Auditus-iERP</div>
            <h2 className="section-h2">The ERP That <span>Actually Works</span><br />for Indian Business</h2>
            <p className="section-desc">
              Built from scratch for Indian retail workflows — not adapted from foreign software.
              Every feature is designed keeping Indian GST, Indian payment methods, and Indian
              business culture in mind.
            </p>
            <img
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=700&q=80&fit=crop"
              alt="Business team reviewing ERP dashboard"
              className="why-img reveal"
            />
          </div>
          <div className="why-right">
            {WHY_ITEMS.map((w, i) => (
              <div key={w.title} className={`why-card reveal`} style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="why-icon-wrap">
                  <FontAwesomeIcon icon={w.icon} className="why-icon" />
                </div>
                <div>
                  <h3 className="why-title">{w.title}</h3>
                  <p className="why-desc">{w.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FEATURE SHOWCASE (alternating) ───────────────────── */}
      <section className="showcase-section">
        <div className="section-header reveal">
          <div className="section-tag">Feature Deep Dive</div>
          <h2 className="section-h2">See What Makes Us <span>Different</span></h2>
        </div>

        {[
          {
            title: "Real-Time Multi-Branch Dashboard",
            desc: "See live sales, stock, and profit across all your branches on one screen. Compare performance, transfer stock, and manage approvals — all without leaving the dashboard.",
            points: ["Consolidated P&L across branches", "Inter-branch stock transfer", "Branch-wise target vs achievement", "One-click GST consolidation"],
            img: "https://images.klipfolio.com/website/public/7f800f64-5c5c-4bd7-b2b9-0a9c295f9ae5/hotel-dashboard.png",
            imgAlt: "Analytics dashboard on multiple screens",
          },
          {
            title: "WhatsApp-First Communication",
            desc: "The only ERP that lets you send invoices, payment reminders, loyalty offers, and business reports directly via WhatsApp — no extra app needed.",
            points: ["Auto-send bills after billing", "Payment collection reminders", "Promotional broadcast campaigns", "Daily owner summary report"],
            img: "https://images.unsplash.com/photo-1611746872915-64382b5c76da?w=700&q=80&fit=crop",
            imgAlt: "Person using WhatsApp on smartphone for business",
          },
          {
            title: "GST Compliance & E-Invoicing",
            desc: "Stay 100% GST compliant with automated GSTR-1, GSTR-3B, e-invoice, and e-way bill generation. Built to handle every GST scenario Indian retail throws at you.",
            points: ["Auto GSTR-1 & GSTR-3B", "IRN-based e-invoice generation", "HSN/SAC auto-mapping", "Input Tax Credit tracker"],
            img: "https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=700&q=80&fit=crop",
            imgAlt: "Accountant working on GST and tax documents",
          },
        ].map((f, i) => (
          <div key={f.title} className={`showcase-item ${i % 2 === 1 ? "reverse" : ""} reveal`}>
            <div className="showcase-img-wrap">
              <img src={f.img} alt={f.imgAlt} className="showcase-img" />
              <div className="showcase-img-overlay" />
            </div>
            <div className="showcase-content">
              <h3 className="showcase-title">{f.title}</h3>
              <p className="showcase-desc">{f.desc}</p>
              <ul className="showcase-points">
                {f.points.map((pt) => (
                  <li key={pt}>
                    <FontAwesomeIcon icon={faCheckCircle} className="check-icon" />
                    {pt}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))}
      </section>

      {/* ── TESTIMONIALS ─────────────────────────────────────── */}
      <section className="testimonials-section">
        <div className="section-header reveal">
          <div className="section-tag">Customer Stories</div>
          <h2 className="section-h2">Trusted by <span>5,000+ Businesses</span> Across India</h2>
        </div>
        <div className="testimonials-track">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={t.name}
              className={`testimonial-card ${i === activeTestimonial ? "active" : ""}`}
              onClick={() => setActiveTestimonial(i)}
            >
              <div className="testimonial-stars">
                {Array(t.stars).fill(0).map((_, si) => (
                  <FontAwesomeIcon key={si} icon={faStar} className="star-icon" />
                ))}
              </div>
              <p className="testimonial-text">"{t.text}"</p>
              <div className="testimonial-meta">
                <div className="testimonial-avatar">{t.name.charAt(0)}</div>
                <div>
                  <div className="testimonial-name">{t.name}</div>
                  <div className="testimonial-biz">{t.business}</div>
                </div>
                <span className="testimonial-industry">{t.industry}</span>
              </div>
            </div>
          ))}
        </div>
        <div className="testimonial-dots">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              className={`t-dot ${i === activeTestimonial ? "active" : ""}`}
              onClick={() => setActiveTestimonial(i)}
            />
          ))}
        </div>
      </section>

   

      {/* ── CTA SECTION ──────────────────────────────────────── */}
      <section className="cta-section">
        <div className="cta-bg-glow" />
        <div className="cta-inner reveal">
          <div className="section-tag light">Limited Time Offer</div>
          <h2 className="cta-h2">Start Your Free 7-Day Trial<br /><span>No Credit Card Required</span></h2>
          <p className="cta-sub">
            Join 5,000+ businesses already running smarter with Auditus-iERP.
            Free setup, free data migration, free training included.
          </p>
          <div className="cta-actions">
            <a href="/Demo" className="btn-primary large">
              Book Free Demo <FontAwesomeIcon icon={faArrowRight} />
            </a>
            <a href="tel:+919032032612" className="btn-outline light large">
              Call Us Now
            </a>
          </div>
          <div className="cta-trust-row">
            {["Free Setup", "Free Migration", "Free Training", "Cancel Anytime"].map((t) => (
              <span key={t} className="cta-trust-item">
                <FontAwesomeIcon icon={faCheckCircle} className="cta-check" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

    </div>
  );
}